import React from 'react'

import { Button, ButtonSizes, ButtonVariants } from '@inno-ui/button'
import type { ButtonProps } from '@inno-ui/button'
import {
  CalendarLineIcon,
  EnvelopeLineIcon,
  PhoneLineIcon,
  SpeechbubbleOnSpeechbubbleLineIcon
} from '@inno-ui/icons'
import { useTranslation } from 'react-i18next'

import { CONTACTS_KEY_PREFIX } from '../../InnoscriptaContacts.constants'
import type { ContactEntry } from '../../InnoscriptaContacts.types'
import defaultAvatar from '../../assets/default-avatar.jpg'
import styles from './ContactRow.module.scss'

interface ContactActionProps {
  /** Omitted or empty when the contact cannot be reached this way. */
  href?: string | null
  label: string
  testId: string
  icon: ButtonProps['icon']
  newTab?: boolean
  variant?: ButtonVariants
}

/**
 * Uses the same standard button for available and unavailable contact methods.
 */
const ContactAction: React.FC<ContactActionProps> = ({
  href,
  label,
  testId,
  icon,
  newTab = false,
  variant = ButtonVariants.Ghost
}) => (
  <Button
    type='button'
    icon={icon}
    label={label}
    isHiddenLabel
    size={ButtonSizes.M}
    variant={variant}
    data-testid={testId}
    disabled={!href}
    aria-label={label}
    title={label}
    onClick={() => {
      if (href)
        window.open(href, newTab ? '_blank' : '_self', 'noopener,noreferrer')
    }}
  />
)

interface ContactRowProps {
  contact: ContactEntry
  onChatClick: (authId: number) => void
}

export const ContactRow: React.FC<ContactRowProps> = ({
  contact,
  onChatClick
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: CONTACTS_KEY_PREFIX
  })

  const { responsible, roleKey, isMainContact } = contact
  const { user } = responsible
  const role = isMainContact
    ? `${t(roleKey)} • ${t('main_contact')}`
    : t(roleKey)
  const hasContactId = user.id !== null

  return (
    <div className={styles.contactRow}>
      <img
        className={styles.avatar}
        src={user.avatar ?? defaultAvatar}
        alt={user.name}
      />

      <div className={styles.details}>
        <p className={styles.name}>{user.name}</p>
        <p className={styles.role} data-testid='contact-role'>
          {role}
        </p>
      </div>

      {hasContactId && (
        <div className={styles.actions}>
          <Button
            type='button'
            icon={SpeechbubbleOnSpeechbubbleLineIcon}
            label={t('actions.chat', { name: user.name })}
            isHiddenLabel
            size={ButtonSizes.M}
            variant={ButtonVariants.Ghost}
            data-testid='contact-chat'
            onClick={() => {
              if (user.id !== null) onChatClick(user.id)
            }}
            aria-label={t('actions.chat', { name: user.name })}
            title={t('actions.chat', { name: user.name })}
          />

          <ContactAction
            testId='contact-email'
            href={user.email && `mailto:${user.email}`}
            label={t('actions.email', { name: user.name })}
            icon={EnvelopeLineIcon}
          />

          <ContactAction
            testId='contact-phone'
            href={user.working_phone && `tel:${user.working_phone}`}
            label={t('actions.call', { name: user.name })}
            icon={PhoneLineIcon}
          />

          {contact.appointlyUrl && (
            <ContactAction
              testId='contact-appointly'
              href={contact.appointlyUrl}
              newTab
              variant={ButtonVariants.Light}
              label={t('actions.appointly', { name: user.name })}
              icon={CalendarLineIcon}
            />
          )}
        </div>
      )}
    </div>
  )
}
